import {
  Autocomplete,
  type AutocompleteProps,
  Button,
  Stack,
  TextField,
} from '@mui/material'
import { type FormEvent, useRef } from 'react'
import {
  GaForm,
  type GaFormHandle,
  type GaFormProps,
} from '@/features/analytics/gaForm/GaForm'
import { useDeferredGaSubmit } from '@/features/analytics/gaForm/useDeferredGaSubmit'
import {
  useLazyParseSearchStringQuery,
  useSavedSearchQuery,
  useSearchQuery,
  useSearchRecentQuery,
} from '@/features/search/api'
import { useSavedSearch } from '@/features/search/hooks/useSavedSearch'
import { useSearch } from '@/features/search/hooks/useSearch'
import type { SavedSearch } from '@/features/search/types'
import { useAppSelector } from '@/redux/hooks'
import { searchSelectors } from '../../slice'
import { buildSearchGaFormProps } from '../../utils'
import { SavedSearchMenuButton } from './SavedSearchMenuButton'

const baseGaFormProps: Omit<GaFormProps, 'children'> = {
  formName: 'saved search',
  formId: 'saved-search',
  formLocation: 'main',
  formDestination: 'emc/search',
  formSubmitText: 'apply',
}

export const SavedSearchSelect = () => {
  const gaFormRef = useRef<GaFormHandle>(null)
  const { data: savedSearches = [], ...savedSearchesRequest } =
    useSavedSearchQuery()
  const { setSelectedSavedSearch, getCurrentSavedSearch } = useSavedSearch()
  const currentSavedSearch = getCurrentSavedSearch()

  const { setNext, applyNextSearch } = useSearch()
  const searchType = useAppSelector(searchSelectors.selectSearchType)
  const payload = useAppSelector(searchSelectors.selectSearchPayload)
  const currentSavedSearchState = useAppSelector(
    searchSelectors.selectCurrentSavedSearch,
  )
  const isSearch = searchType === 'standard' && payload
  const isRecent = searchType === 'recent'
  const { data: searchResult, ...searchRequest } = useSearchQuery(payload, {
    skip: !isSearch,
  })
  const { data: recentResult, ...recentRequest } = useSearchRecentQuery(
    { skip: payload?.options.skip ?? 0, take: payload?.options.size ?? 25 },
    { skip: !isRecent },
  )
  const requestState = isSearch
    ? searchRequest
    : isRecent
      ? recentRequest
      : null
  const totalCount = isSearch
    ? (searchResult?.totalCount ?? 0)
    : isRecent
      ? (recentResult?.totalCount ?? 0)
      : 0
  const isError = requestState?.isError ?? false

  const [parseCriteriaString, parseRequest] = useLazyParseSearchStringQuery()

  const { markPending } = useDeferredGaSubmit(gaFormRef, requestState)

  const handleSubmit = (_event?: FormEvent<HTMLFormElement>) => {
    if (!currentSavedSearch) return
    markPending()

    parseCriteriaString(currentSavedSearch.criteria, true)
      .unwrap()
      .then(setNext)
      .then(applyNextSearch)
      .catch((error) => {
        console.error('Failed to parse selected saved search criteria', error)
      })
  }

  const handleSelect: AutocompleteProps<
    SavedSearch,
    false,
    false,
    false
  >['onChange'] = (_event, value: SavedSearch | null, _reason) => {
    if (value) {
      setSelectedSavedSearch(value)
      parseCriteriaString(value.criteria)
        .unwrap()
        .then(setNext)
        .catch((error) => {
          console.error('Failed to parse saved search criteria preview', error)
        })
    } else {
      setSelectedSavedSearch(null)
    }
  }

  const gaFormProps: Omit<GaFormProps, 'children'> = {
    ...buildSearchGaFormProps({
      baseGaFormProps,
      onSubmit: handleSubmit,
      formStatus: isError ? 'error' : 'success',
      payload,
      totalCount,
    }),
    getFormContent: () =>
      currentSavedSearchState?.name
        ? `Saved Search: ${currentSavedSearchState.name}`
        : null,
  }

  return (
    <GaForm ref={gaFormRef} {...gaFormProps}>
      <Stack direction="row">
        <Autocomplete
          loading={savedSearchesRequest.isFetching}
          color="secondary"
          disablePortal
          fullWidth
          options={savedSearches ?? []}
          getOptionKey={(i: SavedSearch) => i.code}
          getOptionLabel={(i: SavedSearch) => i.name}
          onChange={handleSelect}
          value={currentSavedSearch}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                sx={{
                  '& .MuiInputBase-root': {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
                color="secondary"
                label="Saved Search"
                placeholder="Saved Search"
              />
            )
          }}
        />
        <Button
          name="saved-search"
          title="load"
          disabled={!currentSavedSearch}
          disableElevation
          variant="contained"
          color="secondary"
          type="submit"
          sx={{ height: 'auto', borderRadius: 0 }}
          loading={parseRequest.isFetching}
        >
          {parseRequest.isFetching ? 'Loading' : 'Apply'}
        </Button>
        <SavedSearchMenuButton />
      </Stack>
    </GaForm>
  )
}
